import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useAuth } from '../hooks/useAuth';
import ChatWindow from '../components/organisms/ChatWindow';
import DynamicContentArea from '../components/organisms/DynamicContentArea';
import { TaskData } from '../components/molecules/TaskStatus';
import { useParams, useLocation } from 'react-router-dom';

const AgentPage = () => {
  const { user, logout } = useAuth();
  const { candidateId } = useParams();
  const location = useLocation();
  const [candidate, setCandidate] = useState<any>(location.state?.candidate || null);
  const [chatWidth, setChatWidth] = useState(400);
  const [isContentLoading, setIsContentLoading] = useState(false);
  const [lastChatMessage, setLastChatMessage] = useState<string>('');
  const [selectedContentId, setSelectedContentId] = useState<string>('default');
  const [agentHtmlContent, setAgentHtmlContent] = useState<string>('');

  // Fallback: If candidateId is present but candidate is not, fetch or set placeholder
  useEffect(() => {
    if (candidateId && !candidate) {
      // TODO: Replace with real API call if needed
      setCandidate({
        id: candidateId,
        name: `Candidate ${candidateId}`,
        role: 'Unknown',
        email: 'unknown@email.com',
      });
    }
  }, [candidateId, candidate]);

  const handleChatResize = (width: number) => setChatWidth(width);

  const handleMessageSend = (message: string) => {
    console.log('[AgentPage] User sent message:', message);
    
    // Clear explicit content selection to allow API-based content matching
    setSelectedContentId('');
    
    // Show loader in right panel while APIs are processing
    setIsContentLoading(true);
    setAgentHtmlContent(''); // Clear previous content
    
    // Update the last chat message
    setLastChatMessage(message);
  };

  const handleTaskUpdate = (tasks: TaskData[]) => {};

  // Callback when content loading is complete (called by DynamicContentArea)
  const handleContentLoadComplete = () => {
    // Do not clear the right pane loader here.
    // We keep the loader until /agent/message returns and onContentUpdate provides HTML.
  };

  // Function to manually set content (can be used for buttons or specific triggers)
  const handleContentChange = (contentId: string) => {
    console.log('Switching to content:', contentId);
    setSelectedContentId(contentId);
    setLastChatMessage(''); // Clear chat message to use contentId instead
    setIsContentLoading(true);
  };

  // Function to handle action clicks
  const handleActionClick = (action: string, data?: any) => {
    console.log('Action clicked:', action, data);
    
    // Handle different actions
    switch (action) {
      case 'show-recommendations':
        // You can create a custom content block or show a modal
        alert('Smart Recommendations feature coming soon!');
        break;
      default:
        console.log('Unknown action:', action);
    }
  };

  // Handle content update from agent chat API
  const handleContentUpdate = (htmlContent: string) => {
    console.log('[AgentPage] Received HTML content from agent:', htmlContent.substring(0, 100));
    setAgentHtmlContent(htmlContent);
    setIsContentLoading(false);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', background: 'var(--light-gray)', overflow: 'hidden' }}>
      {/* Candidate Info Banner */}
      {candidateId && candidate && (
        <Box sx={{ 
          background: 'var(--white)', 
          px: 3, 
          py: 2, 
          borderBottom: '1px solid #e0e7ff',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'var(--primary-blue)' }}>
            👤 <strong>Candidate:</strong> {candidate.name} | <strong>Role:</strong> {candidate.role} | <strong>Email:</strong> {candidate.email} | <strong>Job Description:</strong> {candidate.jobId}
          </Typography>
        </Box>
      )}

      {/* Main Content Area - Fills remaining viewport height */}
      <Box sx={{ 
        display: 'flex',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden'
      }}>
        {/* Left Panel - Chat */}
        <Box
          sx={{
            width: chatWidth,
            minWidth: 300,
            maxWidth: 800,
            borderRight: '1px solid #e0e7ff',
            background: 'var(--white)',
            position: 'relative',
            height: '100%',
            overflow: 'hidden',
          }}
        >
          <ChatWindow
            onMessageSend={handleMessageSend}
            onTaskUpdate={handleTaskUpdate}
            onContentUpdate={handleContentUpdate}
            sx={{ height: '100%' }}
            candidate={candidateId ? candidate : undefined}
            candidateId={candidateId}
          />
        </Box>
        
        {/* Right Panel - Content */}
        <Box
          sx={{
            flex: 1,
            background: 'var(--light-gray)',
            position: 'relative',
            height: '100%',
            overflow: 'hidden',
          }}
        >
        <DynamicContentArea
          isLoading={isContentLoading}
          candidate={candidate}
          candidateId={candidateId}
          currentMessage={lastChatMessage}
          contentId={selectedContentId}
          htmlContent={agentHtmlContent}
          onContentChange={handleContentChange}
          onActionClick={handleActionClick}
          onLoadComplete={handleContentLoadComplete}
          sx={{ height: '100%' }}
        />
        </Box>
      </Box>
    </Box>
  );
};

export default AgentPage;
